import { styled } from 'styled-components';
import { useBrand } from '@/hooks/useBranding';
import { useDarkMode } from '@/contexts/DarkModeContext';

const LogoContainer = styled.div<{ $isCollapsed: boolean }>`
  ${({ theme, $isCollapsed }) => `
    display: flex;
    align-items: center;
    justify-content: ${$isCollapsed ? 'center' : 'flex-start'};
    padding: ${
      $isCollapsed
        ? `${theme.paddingSM}px`
        : `${theme.paddingSM}px ${theme.paddingLG}px`
    };
    margin-bottom: ${theme.marginXS}px;
    border-bottom: ${theme.lineWidth}px solid ${theme.colorBorderSecondary || theme.colorBorder};
    background: ${theme.colorBgContainer};
    min-height: ${theme.sizeXXL}px;
    position: relative;
  `}
`;

const LogoImage = styled.img<{ $isCollapsed: boolean }>`
  ${({ theme, $isCollapsed }) => `
    height: ${
      $isCollapsed
        ? `${theme.controlHeightLG - 4}px`
        : `${theme.controlHeight}px`
    };
    width: auto;
    transition: height ${theme.motionDurationMid} ${theme.motionEaseInOut};
    flex-shrink: 0;
  `}
`;

const LogoText = styled.span<{ $isCollapsed: boolean }>`
  ${({ theme, $isCollapsed }) => `
    font-size: ${`${theme.fontSizeLG}px`};
    font-weight: ${theme.fontWeightStrong};
    color: ${theme.colorText};
    margin-left: ${`${theme.marginSM}px`};
    opacity: ${$isCollapsed ? 0 : 1};
    visibility: ${$isCollapsed ? 'hidden' : 'visible'};
    transition:
      opacity ${theme.motionDurationMid} ${theme.motionEaseInOut},
      visibility ${theme.motionDurationMid} ${theme.motionEaseInOut};
    ${theme.motionEaseInOut};
    white-space: nowrap;
    overflow: hidden;
    width: ${$isCollapsed ? '0' : 'auto'};
  `}
`;

interface LogoProps {
  isCollapsed: boolean;
}

export const Logo = ({ isCollapsed }: LogoProps) => {
  const brand = useBrand();
  const [isDarkMode] = useDarkMode();
  
  // Select appropriate logo based on dark mode
  const logoSrc = isDarkMode && brand.assets.logo.dark 
    ? brand.assets.logo.dark 
    : brand.assets.logo.light;
  
  // Check if we should show the logo text
  const showLogoText = brand.features?.showLogo !== false;
  
  return (
    <LogoContainer $isCollapsed={isCollapsed}>
      <LogoImage 
        src={logoSrc} 
        alt={brand.displayName} 
        $isCollapsed={isCollapsed} 
      />
      {!isCollapsed && showLogoText && (
        <LogoText $isCollapsed={isCollapsed}>
          {brand.displayName}
        </LogoText>
      )}
    </LogoContainer>
  );
};
